import React, { useState, useEffect, useMemo } from 'react';
import { useTask } from '../contexts/TaskContext';
import BucketListItem from './BucketListItem';
import BucketListSettingsModal from './BucketListSettingsModal';
import BucketListCompletedModal from './BucketListCompletedModal';

// 5年刻みのグループキー: 例 35 → "35-39歳"
function groupKey(age) {
  const start = Math.floor(age / 5) * 5;
  return `${start}-${start + 4}歳`;
}

// 目標年齢でソートしてグループ化
function buildGroups(activeItems) {
  // 期限の早い順＝目標年齢の小さい順
  const sorted = [...activeItems].sort((a, b) => {
    const ay = (Number(a.targetYears) || 0) + (Number(a.targetMonths) || 0) / 12;
    const by = (Number(b.targetYears) || 0) + (Number(b.targetMonths) || 0) / 12;
    return ay - by;
  });

  const map = new Map();
  for (const item of sorted) {
    const key = groupKey(Number(item.targetYears) || 0);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  // Map の挿入順はソート済みの順序が保たれる
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
}

export default function BucketListView() {
  const { bucketItems, birthdate, completeBucket } = useTask();
  const [showSettings, setShowSettings] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  // 30秒ごとに「今」を更新→残り日数が日跨ぎで自動更新される
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const activeItems = useMemo(
    () => bucketItems.filter((it) => !it.completed),
    [bucketItems]
  );
  const groups = useMemo(() => buildGroups(activeItems), [activeItems]);
  const completedCount = bucketItems.filter((it) => it.completed).length;

  return (
    <div className="task-column column-purple bucket-list">
      <div className="column-header">
        <div className="column-title-row">
          <span className="column-icon">🎯</span>
          <h2 className="column-title">バケットリスト</h2>
        </div>
        {activeItems.length > 0 && (
          <span className="column-count">{activeItems.length}</span>
        )}
      </div>

      <div className="bucket-action-row">
        <button
          type="button"
          className="recur-open-btn bucket-settings-btn"
          onClick={() => setShowSettings(true)}
        >
          <span className="recur-open-icon" aria-hidden="true">🎯</span>
          バケットリスト設定
        </button>

        <button
          type="button"
          className="recur-open-btn bucket-completed-btn"
          onClick={() => setShowCompleted(true)}
        >
          <span className="recur-open-icon" aria-hidden="true">📋</span>
          達成済み{completedCount > 0 ? `（${completedCount}）` : ''}
        </button>
      </div>

      <div className="bucket-groups">
        {!birthdate && (
          <div className="bucket-empty">
            <span className="empty-icon">🎂</span>
            <p>「バケットリスト設定」から生年月日を登録してください</p>
          </div>
        )}

        {birthdate && groups.length === 0 && (
          <div className="bucket-empty">
            <span className="empty-icon">✨</span>
            <p>達成したい目標を追加しましょう</p>
          </div>
        )}

        {groups.map((g) => (
          <div key={g.label} className="bucket-group">
            <h3 className="bucket-group-title">{g.label}</h3>
            <div className="bucket-group-items">
              {g.items.map((item) => (
                <BucketListItem
                  key={item.id}
                  item={item}
                  birthdate={birthdate}
                  now={now}
                  onComplete={completeBucket}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <BucketListSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
      <BucketListCompletedModal
        isOpen={showCompleted}
        onClose={() => setShowCompleted(false)}
      />
    </div>
  );
}
